import {ListTab} from '../common/ListTab';
import {useEffect, useRef, useState} from 'react';
import {
  MAIN_LIST_CNT,
  DIRECTORY_CONTENT_INIT_LIST,
  DIRECTORY_LIVE_CONTEXT_MENU,
  DIRECTORY_VOD_CONTEXT_MENU,
  DIRECTORY_LIST_CNT,
} from '@/constants/main';
import DirectoryApi from '@/api/directory/directory';
import Catch from '@/components/my/common/item/MyCatch';
import {isLogin, sendWebUvLog} from '@/utils/main';
import CategoryApi from '@/api/directory/category';
import useDirectoryStore from '@/stores/directory';
import LogApi from '@/api/common/log';
import {useHiddenStreamer} from '../../common/hooks/useHiddenStreamer';
import {BroadcastLayout, BroadcastType} from '../../common/Broadcast';
import ErrorBoundary from '../category/ErrorBoundary';
import {SoopLoadingSpinner} from '@/components/ui';
import useGlobalUIStore from '@/stores';
import {useNavigate} from 'react-router-dom';
import {BROADCAST_TYPES} from '@/constants';

/**
 * [탐색 -> 어드민 칩 선택] 영역
 * @param {Object} params
 * @returns
 */
export const AdminCategoryContent = ({categoryName, categoryType}) => {
  const {
    selectedCategoryTab,
    selectedCategoryInfo,
    selectedContentFilter,
    selectedPeriodFilter,
    isCategoryInfoLoaded,
    setSelectedCategoryTab,
    setSelectedContentFilterByTab,
    setSelectedPeriodFilter,
  } = useDirectoryStore();
  const {
    remainCount,
    slicedBroadList,
    setRemainCount,
    filterBroadList,
    setSlicedBroadList,
  } = useHiddenStreamer();
  const {refreshTimestamp, setRefreshTimestamp} = useGlobalUIStore();
  const [isLoading, setIsLoading] = useState(false);
  const [contentList, setContentList] = useState(DIRECTORY_CONTENT_INIT_LIST);
  const [hiddenStreamer, setHiddenStreamer] = useState([]);

  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState(
    selectedContentFilter['live']?.[0]?.type || 'view_cnt_desc',
  );

  /** 현재 카테고리(어드민 칩) 정보 상태 변경에 반응 */
  useEffect(() => {
    const {contentsDisplay} = selectedCategoryInfo;

    /**
     * 카테고리 정보 변경 시 노출 우선순위에 따라 탭 자동 전환 함수
     */
    const handleTabSelection = (currentTab, fallbackTab1, fallbackTab2) => {
      const navigateToTab = (tab) => {
        setSelectedCategoryTab(tab);
        navigate(
          `/directory/category/${encodeURIComponent(categoryName)}/${tab}`,
          {
            replace: true, // 자동 변환에 의한 이동이기 때문에 히스토리 남기지 않음
          },
        );
      };

      if (!contentsDisplay[currentTab]) {
        if (contentsDisplay[fallbackTab1]) {
          navigateToTab(fallbackTab1);
        } else {
          navigateToTab(fallbackTab2);
        }
      } else {
        setSelectedCategoryTab(currentTab);
      }
    };

    // 노출 우선순위 별로 선택된 탭을 조정
    switch (categoryType) {
      case 'live':
        handleTabSelection('live', 'vod', 'catch');
        break;
      case 'vod':
        handleTabSelection('vod', 'live', 'catch');
        break;
      case 'catch':
        handleTabSelection('catch', 'live', 'vod');
        break;
      default:
        break;
    }
  }, [
    categoryName,
    categoryType,
    navigate,
    selectedCategoryInfo,
    setSelectedCategoryTab,
  ]);

  useEffect(() => {
    if (remainCount > 0) {
      setPage((prev) => prev + 1);
    }
  }, [remainCount]);

  useEffect(() => {
    if (hiddenStreamer.length > 0) {
      getContentsList();
    }
  }, [hiddenStreamer]);

  useEffect(() => {
    if (refreshTimestamp) {
      window.scrollTo(0, 0);
    }
  }, [refreshTimestamp]);

  /**
   * [페이지, 정렬 정보, 필터 정보, 선택된 어드민 칩, 카테고리 탭]에 반응한다.
   * 방송/영상 목록을 업데이트한다.
   */
  useEffect(() => {
    if (page === 1) {
      setIsLoading(true);
    }
    if (selectedCategoryInfo.type === 'admin') {
      if (page === 1) {
        setIsLoading(true);
      }
      if (contentList.isMore) {
        if (isLogin()) {
          getHiddenStreamerList();
        } else {
          getContentsList();
        }
      } else {
        if (slicedBroadList.length > 0 && page > 1) {
          setContentList({
            ...contentList,
            list: [...contentList.list, ...slicedBroadList],
          });
          setSlicedBroadList([]);
        } else {
          if (isLogin()) {
            getHiddenStreamerList();
          } else {
            getContentsList();
          }
        }
      }
    }
  }, [
    page,
    order,
    refreshTimestamp,
    selectedCategoryInfo.menuId,
    selectedCategoryTab,
    selectedContentFilter,
    selectedPeriodFilter,
  ]);

  const handleLiveViewLog = (response) => {
    try {
      const params = {
        path1: 'directory',
        path2: 'admin',
        listSendType: 'client',
        directoryAdminId: selectedCategoryInfo.adminId,
        listViewCnt: response.list.length,
        listViewData: response.list.map((item) => ({
          bno: item.broadNo,
        })),
      };
      LogApi.sendLiveListViewLog(params);
    } catch (error) {}
  };

  const handleVodViewLog = (response) => {
    try {
      const params = {
        path1: 'directory',
        path2: 'admin',
        listSendType: 'client',
        directoryAdminId: selectedCategoryInfo.adminId,
        listViewCnt: response.list.length,
        listViewData: response.list.map((item) => ({
          vno: item.titleNo,
        })),
      };
      LogApi.sendVodListViewLog(params);
    } catch (error) {}
  };

  /**
   * 아이템 데이터 가져오기 요청에 대한 식별자 기록. Race Condition에 대한 방지책
   */
  const latestRequestRef = useRef(0);

  /** 아이템 리스트에 표시할 데이터들을 불러오는 함수 */
  const getContentsList = async () => {
    latestRequestRef.current += 1;
    const currentRequest = latestRequestRef.current;

    try {
      const queryParams = createQueryParams();

      // 카테고리별 컨텐츠 표시 여부 체크
      if (!isValidCategoryDisplay() || !queryParams) {
        return;
      }
      const response = await DirectoryApi.getContentsList(queryParams);
      if (currentRequest === latestRequestRef.current && response) {
        // 가장 최근 발송된 요청의 응답만 사용
        handleContentList(response);
        logViewEvent(response);
      }
    } catch (error) {
      setContentList(DIRECTORY_CONTENT_INIT_LIST);
    } finally {
      setIsLoading(false);
    }
  };

  /** 현재 컴포넌트의 상태를 조합해 쿼리 파라미터를 생성하는 함수 */
  const createQueryParams = () => {
    const baseParams = {
      szMenuId: selectedCategoryInfo.menuId,
      szType: selectedCategoryTab,
      nPageNo: page,
      nListCnt:
        selectedCategoryTab === 'live' ? MAIN_LIST_CNT : DIRECTORY_LIST_CNT,
      szPlatform: 'pc',
    };

    if (selectedCategoryTab === 'live') {
      baseParams.szOrder = order;
    } else {
      baseParams.szOrder = selectedContentFilter[selectedCategoryTab].find(
        (item) => item.key === 'sort',
      )?.type;
      baseParams.szTerm = selectedPeriodFilter.type;

      if (selectedCategoryTab === 'vod') {
        baseParams.szFileType = selectedContentFilter[selectedCategoryTab].find(
          (item) => item.key === 'fileType',
        )?.type;
      }
      if (baseParams.szTerm === 'directInput') {
        const {startDate, endDate, applyStatus} = selectedPeriodFilter;
        baseParams.szStartDate = startDate;
        baseParams.szEndDate = endDate;
        if (!applyStatus) {
          return;
        }
      }
    }

    return baseParams;
  };

  // 카테고리별 컨텐츠 표시 여부 확인
  const isValidCategoryDisplay = () => {
    const displaySettings = selectedCategoryInfo.contentsDisplay;
    if (selectedCategoryTab === 'live') {
      return displaySettings.live;
    } else if (selectedCategoryTab === 'vod') {
      return displaySettings.vod;
    } else if (selectedCategoryTab === 'catch') {
      return displaySettings.catch;
    }
    return false;
  };

  // Response 데이터 처리
  const handleContentList = (response) => {
    const {isMore, list} = response;
    if (hiddenStreamer.length > 0) {
      filterBroadList(
        selectedCategoryTab,
        page,
        list,
        isMore,
        hiddenStreamer,
        setContentList,
      );
    } else {
      setContentList((prevContentList) => ({
        isMore,
        list: page === 1 ? list : [...prevContentList.list, ...list],
      }));
    }
  };

  /** 어드민 칩 / 탭 변경시 리스트 상태 초기화 */
  useEffect(() => {
    setPage(1);
    setRemainCount(0);
    setSlicedBroadList([]);
    setContentList(DIRECTORY_CONTENT_INIT_LIST);
  }, [categoryType, selectedCategoryInfo, setRemainCount, setSlicedBroadList]);

  /** 로그 전송 처리 */
  const logViewEvent = (response) => {
    if (response?.list?.length > 0) {
      if (selectedCategoryTab === 'live') {
        handleLiveViewLog(response);
      } else if (['vod', 'catch'].includes(selectedCategoryTab)) {
        handleVodViewLog(response);
      }
    }
  };

  const getHiddenStreamerList = async () => {
    try {
      const response = await CategoryApi.getHiddenStreamerList();
      if (response && response.result === 1) {
        if (response.data.length === 0) {
          getContentsList();
        } else {
          setHiddenStreamer([...response.data]);
        }
      }
    } catch (error) {
      getContentsList();
    }
  };

  const refreshTimer = useRef(false);

  /** 탭 클릭 시 동작 */
  const handleOnClickTab = (type) => {
    if (type === selectedCategoryTab) {
      if (refreshTimer.current) {
        return;
      }
      setRefreshTimestamp();
      refreshTimer.current = true;
      setTimeout(() => {
        refreshTimer.current = false;
      }, 1500);
    }

    navigate(`/directory/category/${selectedCategoryInfo.menuId}/${type}`, {
      replace: type === selectedCategoryTab,
    });

    setPage(1);
    setRemainCount(0);
    setSlicedBroadList([]);
    setSelectedPeriodFilter({
      key: 'period',
      label: '1주',
      type: '1week',
      applyStatus: false,
      startDate: '',
      endDate: '',
    });
    setContentList(DIRECTORY_CONTENT_INIT_LIST);
    setSelectedCategoryTab(type);
    sendWebUvLog(null);
  };

  const handleOnClickMore = () => {
    setPage((prevPage) => prevPage + 1);
  };

  /** LIVE 탭 필터 선택 아이템 변경 시 동작 */
  const handleOnClickSort = (type) => {
    if (type === order) {
      return;
    }
    setPage(1);
    setOrder(type);
    setSelectedContentFilterByTab('live', [
      {key: 'sort', label: '조회수', type: type},
    ]);
    setRemainCount(0);
    setSlicedBroadList([]);
    setContentList(DIRECTORY_CONTENT_INIT_LIST);
  };

  /** LIVE 외 탭에서 필터 클릭 시 동작 */
  const handleVodFilterClick = (key, label, type) => {
    if (key === 'period') {
      setSelectedPeriodFilter({
        ...selectedPeriodFilter,
        label,
        type,
        applyStatus: false,
      });
    } else {
      setSelectedContentFilterByTab(
        selectedCategoryTab,
        selectedContentFilter[selectedCategoryTab].map((filter) =>
          filter.key === key ? {...filter, label, type} : filter,
        ),
      );
    }

    setPage(1);
    setRemainCount(0);
    setSlicedBroadList([]);
    //직접 입력상태일때는 실제로 날짜 set 시키기 전에는 리스트 초기화 시키지 말라고 정책 내용 반영 (직접입력 탭을 누른 상태이면 유저의 액션의 의해서 탭 이동이니 초기화가 맞는거같은데 이해가 안되긴 함)
    if (type !== 'directInput') {
      setContentList(DIRECTORY_CONTENT_INIT_LIST);
    }
  };

  const listTabProps = {
    order,
    categoryInfo: selectedCategoryInfo,
    isCategoryInfoLoaded,
    handleOnClickTab,
    handleOnClickSort,
    handleVodFilterClick,
  };

  return (
    <>
      <ListTab {...listTabProps} />
      {isLoading ? (
        <SoopLoadingSpinner />
      ) : (selectedCategoryTab === 'live' || selectedCategoryTab === 'vod') &&
        contentList?.list?.length > 0 ? (
        <div className='cBox-list'>
          <ul>
            {contentList?.list?.map((broadcast, broadcastIdx) => (
              <li
                data-type='cBox'
                key={`${broadcast.userId || broadcast.broadcastId}-${broadcastIdx}`}>
                {selectedCategoryTab === 'live' && (
                  <ErrorBoundary fallback={<div>로딩중</div>}>
                    <BroadcastLayout
                      broadcastType={BROADCAST_TYPES.LIVE}
                      liveType={broadcast.broadType}
                      broadcastId={broadcast.broadNo}
                      categoryName={
                        broadcast.categoryTags?.length > 0
                          ? broadcast.categoryTags[0]
                          : ''
                      }
                      categoryNo={broadcast.broadCateNo}
                      categoryTags={broadcast.categoryTags}
                      autoHashTags={broadcast.autoHashtags}
                      hashTags={broadcast.hashTags}
                      canResend={Boolean(broadcast.visitBroadType)}
                      allowsAdult={Number(broadcast.grade) === 19}
                      hasPassword={Boolean(broadcast.isPassword)}
                      hasDrops={Boolean(broadcast.isDrops)}
                      thumbnailUrl={broadcast.thumbnail}
                      title={broadcast.broadTitle}
                      userId={broadcast.userId}
                      userNickname={broadcast.userNick}
                      viewCount={broadcast.viewCnt}
                      startDateTime={broadcast.broadStart}
                      subscriptionOnly={broadcast.subscriptionOnly}
                      contextMenu={{
                        menuList: DIRECTORY_LIVE_CONTEXT_MENU,
                      }}
                      log={{
                        data: {
                          path2: 'admin',
                          directoryAdminId: selectedCategoryInfo.adminId,
                        },
                      }}>
                      <BroadcastType.Live />
                    </BroadcastLayout>
                  </ErrorBoundary>
                )}
                {selectedCategoryTab === 'vod' && (
                  <ErrorBoundary fallback={<div>로딩중</div>}>
                    <BroadcastLayout
                      broadcastType={BROADCAST_TYPES.VOD}
                      broadcastId={broadcast.titleNo}
                      authNumber={broadcast.authNo}
                      vodType={broadcast.fileType}
                      duration={broadcast.vodDuration}
                      categoryName={
                        broadcast.categoryTags?.length > 0
                          ? broadcast.categoryTags[0]
                          : ''
                      }
                      autoHashTags={broadcast.autoHashtags}
                      hashTags={broadcast.hashTags}
                      categoryTags={broadcast.categoryTags}
                      categoryNo={broadcast.category}
                      vodCategoryNo={broadcast.vodCategory}
                      accessibleAge={Number(broadcast.grade)}
                      allowsAdult={
                        Number(broadcast.grade) === 19 &&
                        Number(broadcast.category) === 30000
                      }
                      hasBadge={true}
                      thumbnailUrl={broadcast.thumbnail}
                      title={broadcast.title}
                      isPpv={broadcast.ppv}
                      userId={broadcast.userId}
                      userNickname={broadcast.userNick}
                      originalUserId={broadcast.originalUserId}
                      originalUserNickname={broadcast.originalUserNick}
                      viewCount={broadcast.viewCnt}
                      createdDate={broadcast.regDate}
                      previewThumbnailUrl={broadcast.webpPath}
                      profileImageUrl={broadcast.userProfileImg}
                      log={{
                        data: {
                          path2: 'admin',
                          directoryAdminId: selectedCategoryInfo.adminId,
                        },
                      }}
                      contextMenu={{menuList: DIRECTORY_VOD_CONTEXT_MENU}}>
                      <BroadcastType.Vod />
                    </BroadcastLayout>
                  </ErrorBoundary>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : selectedCategoryTab === 'catch' && contentList?.list?.length > 0 ? (
        <div className='catch_area'>
          <div className='catch_list'>
            <ul>
              {contentList?.list?.map((catchItem, catchIdx) => (
                <li key={`${catchItem.catchId}-${catchIdx}`}>
                  <Catch
                    catchId={catchItem.titleNo}
                    thumbnailSrc={catchItem.thumbnail}
                    isAdult={Number(catchItem.grade) === 19}
                    category={catchItem.category}
                    vodCategory={catchItem.vodCategory}
                    isHorizontal={catchItem.resolutionType === 'horizontal'}
                    viewCount={catchItem.viewCnt}
                    {...catchItem}
                    isLogSendData={{
                      path2: 'admin',
                      directoryAdminId: selectedCategoryInfo.adminId,
                      ...(catchItem.category && {
                        exCategoryNo: catchItem.category,
                      }),
                    }}
                    catchContextMenuList={[{label: '공유하기', type: 'share'}]}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        contentList?.list?.length === 0 && (
          <div
            className={`noList ${selectedCategoryTab === 'live' ? 'live' : ''}`}>
            {selectedCategoryTab === 'live' ? (
              <>
                <strong>현재 방송 중인 스트리머가 없습니다.</strong>
                <p>대신 VOD, Catch를 탐색 해보세요.</p>
              </>
            ) : (
              <p>
                해당 카테고리의{' '}
                <strong>
                  {selectedCategoryTab === 'catch'
                    ? 'Catch'
                    : selectedCategoryTab.toUpperCase()}
                </strong>
                가 없습니다.
              </p>
            )}
          </div>
        )
      )}
      {(contentList?.isMore || slicedBroadList.length > 0) && (
        <div className='show_more'>
          <button type='button' onClick={handleOnClickMore}>
            더보기
          </button>
        </div>
      )}
    </>
  );
};
